import { ReactNode } from "react";
import { Tooltip } from "antd";
import { GitCommit, Calendar, MessageSquare, ExternalLink } from "lucide-react";
import moment from "moment";
import SafeAvatar from "./SafeAvatar";
import GitCommitLink from "./GitCommitLink";

export interface CommitInfo {
    message?: string;
    sha?: string;
    version?: string;
    branch?: string;
    commitType?: string;
    date?: string | Date;
    repository?: string;
    repositoryName?: string;
    user?: {
        name?: string;
        avatar?: string;
    };
}

export interface CommitTooltipProps {
    children: ReactNode;
    commit: CommitInfo;
    deploymentDate?: Date | string;
}

/**
 * Extracts repository name from a repository URL
 * e.g., "https://github.com/org/repo" -> "org/repo"
 */
const extractRepoName = (repoUrl?: string): string => {
    if (!repoUrl) return "";
    try {
        const url = new URL(repoUrl);
        // Remove leading slash and .git suffix
        return url.pathname.replace(/^\//, "").replace(/\.git$/, "");
    } catch {
        // If not a valid URL, return as-is or extract from path
        return repoUrl.split("/").slice(-2).join("/");
    }
};

const CommitTooltip = ({ children, commit, deploymentDate }: CommitTooltipProps) => {
    // Use commit date if available, otherwise fall back to deployment date
    const displayDate = commit.date || deploymentDate;
    const isDeploymentDateFallback = !commit.date && deploymentDate;
    
    // Get repository name from prop or extract from URL
    const repositoryName = commit.repositoryName || extractRepoName(commit.repository);
    
    // Get commit identifier (sha or version)
    const commitId = commit.sha || commit.version;
    
    // Check if we have minimal required data
    const hasCommitData = commit.message || commitId;
    
    if (!hasCommitData) {
        return <>{children}</>;
    }

    return (
        <Tooltip
            overlayInnerStyle={{
                padding: 0,
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
            overlayStyle={{
                maxWidth: "340px"
            }}
            color="#ffffff"
            title={
                <div style={{
                    fontSize: "13px",
                    color: "#1f2937"
                }}>
                    {/* Commit Message Header */}
                    {commit.message && (
                        <div style={{
                            padding: "14px 16px",
                            borderBottom: "1px solid #f3f4f6",
                            background: "linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%)"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px"
                            }}>
                                <div style={{
                                    flexShrink: 0,
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "8px",
                                    background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <MessageSquare size={14} color="#4f46e5" />
                                </div>
                                <div style={{
                                    flex: 1,
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    lineHeight: "1.5",
                                    color: "#111827",
                                    wordBreak: "break-word"
                                }}>
                                    {commit.message}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Commit Details */}
                    <div style={{ padding: "12px 16px" }}>
                        {/* Branch & SHA */}
                        {(repositoryName || commitId) && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "12px",
                                padding: "8px 10px",
                                background: "#f8fafc",
                                borderRadius: "8px",
                                border: "1px solid #e5e7eb"
                            }}>
                                <GitCommit size={14} color="#6366f1" style={{ flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {repositoryName && (
                                        <div style={{
                                            fontSize: "12px",
                                            color: "#6b7280",
                                            marginBottom: "2px"
                                        }}>
                                            {repositoryName}
                                        </div>
                                    )}
                                    {commitId && (
                                        <div style={{
                                            fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                                            fontSize: "12px",
                                            fontWeight: 500
                                        }}>
                                            <GitCommitLink
                                                version={commitId}
                                                repoUrl={commit.repository}
                                                commitType={commit.commitType}
                                                branch={commit.branch}
                                                hideIcon
                                            />
                                        </div>
                                    )}
                                </div>
                                {commit.repository && (
                                    <a
                                        href={commit.repository}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "6px",
                                            background: "#f3f4f6",
                                            color: "#6b7280",
                                            transition: "all 0.15s ease"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "#e5e7eb";
                                            e.currentTarget.style.color = "#374151";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "#f3f4f6";
                                            e.currentTarget.style.color = "#6b7280";
                                        }}
                                    >
                                        <ExternalLink size={12} />
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Author Info */}
                        {commit.user?.name && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginBottom: displayDate ? "10px" : "0"
                            }}>
                                <SafeAvatar
                                    className="no-border"
                                    src={commit.user.avatar}
                                    size={28}
                                    style={{
                                        border: "2px solid #e5e7eb",
                                        borderRadius: "50%"
                                    }}
                                />
                                <div style={{
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#111827"
                                }}>
                                    {commit.user.name}
                                </div>
                            </div>
                        )}

                        {/* Timestamp */}
                        {displayDate && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "12px",
                                color: "#6b7280",
                                paddingTop: commit.user?.name ? "10px" : "0",
                                borderTop: commit.user?.name ? "1px solid #f3f4f6" : "none"
                            }}>
                                <Calendar size={13} color="#9ca3af" />
                                <span>
                                    {isDeploymentDateFallback && (
                                        <span style={{ color: "#9ca3af", marginRight: "4px" }}>Deployed</span>
                                    )}
                                    {moment(displayDate).fromNow()}
                                    <span style={{ color: "#d1d5db", margin: "0 6px" }}>•</span>
                                    {moment(displayDate).format("HH:mm • DD MMM YY")}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            {children}
        </Tooltip>
    );
};

export default CommitTooltip;
